import { lang, t } from "@/i18n";
import { getAccessToken } from "./auth";

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public details?: string[],
  ) {
    super(message);
  }
}

export async function api<T = unknown>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
  const token = await getAccessToken();
  const headers = new Headers(init.headers);
  if (token) headers.set("Authorization", `Bearer ${token}`);
  headers.set("X-Lang", lang);
  let body = init.body;
  if (init.json !== undefined) {
    headers.set("Content-Type", "application/json");
    body = JSON.stringify(init.json);
  }
  let res: Response;
  try {
    res = await fetch(path, { ...init, headers, body });
  } catch {
    throw new ApiError(t("Нет связи с сервером"), 0);
  }
  if (!res.ok) {
    let msg = t("Ошибка {status}", { status: res.status });
    let details: string[] | undefined;
    try {
      const j = (await res.json()) as { error?: string; details?: string[] };
      msg = j.error ?? msg;
      details = j.details;
    } catch {
      /* not json */
    }
    throw new ApiError(msg, res.status, details);
  }
  const ct = res.headers.get("content-type") ?? "";
  return (ct.includes("json") ? await res.json() : await res.text()) as T;
}

export const get = <T>(p: string) => api<T>(p);
export const post = <T>(p: string, json?: unknown) => api<T>(p, { method: "POST", json: json ?? {} });
export const patch = <T>(p: string, json: unknown) => api<T>(p, { method: "PATCH", json });
export const put = <T>(p: string, json: unknown) => api<T>(p, { method: "PUT", json });
export const del = <T>(p: string) => api<T>(p, { method: "DELETE" });

/**
 * Чтение SSE через fetch (чтобы токен шёл в заголовке, а не в URL).
 * onEvent получает распарсенный JSON из data:.
 */
export async function readSSE(
  path: string,
  init: RequestInit & { json?: unknown },
  onEvent: (event: string, data: unknown) => void,
  signal?: AbortSignal,
) {
  const token = await getAccessToken();
  const headers = new Headers(init.headers);
  if (token) headers.set("Authorization", `Bearer ${token}`);
  headers.set("X-Lang", lang);
  headers.set("Accept", "text/event-stream");
  let body = init.body;
  if (init.json !== undefined) {
    headers.set("Content-Type", "application/json");
    body = JSON.stringify(init.json);
  }
  const res = await fetch(path, { ...init, headers, body, signal, cache: "no-store" });
  if (!res.ok || !res.body) {
    let msg = t("Ошибка {status}", { status: res.status });
    try {
      msg = ((await res.json()) as { error?: string }).error ?? msg;
    } catch {
      /* ignore */
    }
    throw new ApiError(msg, res.status);
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let idx: number;
    while ((idx = buf.search(/\r?\n\r?\n/)) >= 0) {
      const chunk = buf.slice(0, idx);
      buf = buf.slice(idx).replace(/^\r?\n\r?\n/, "");
      let event = "message";
      const data: string[] = [];
      for (const line of chunk.split(/\r?\n/)) {
        if (line.startsWith("event:")) event = line.slice(6).trim();
        else if (line.startsWith("data:")) data.push(line.slice(5).replace(/^ /, ""));
      }
      if (!data.length) continue;
      try {
        onEvent(event, JSON.parse(data.join("\n")));
      } catch {
        /* битый кадр */
      }
    }
  }
}
